import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { SelectChangeEvent } from '@mui/material/Select';
import { RichTextEditor } from './RichTextEditor';
import type {
  TLatestNewsAttachment,
  TLatestNewsItem,
} from './latestNewsStorage';
import {
  getLatestNewsRequiredViolations,
  LATEST_NEWS_TITLE_MAX_LEN,
} from './latestNewsRequiredValidation';
import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';
import {
  useArticlesCreateMutation,
  useArticlesUpdateMutation,
  useArticlesUploadAttachmentMutation,
  useArticlesUploadImageMutation,
  useLazyDropDownListQuery,
} from '~/api/oneBoxV3';
import {
  attachmentsToApi,
  formatArticleBackendValidationMessage,
  latestNewsToCreatePayload,
  latestNewsToUpdatePayload,
  resolveOneBoxV3MediaUrl,
  snapshotLatestNewsItemForEdit,
} from './latestNewsArticleMap';
import {
  LATEST_NEWS_ATTACHMENT_FILE_ACCEPT,
  LATEST_NEWS_COVER_FILE_ACCEPT,
  validateLatestNewsAttachmentPick,
  validateLatestNewsCoverBlobMime,
  validateLatestNewsCoverPick,
} from './latestNewsUploadMime';
import { youtubeEmbedId } from './latestNewsYoutube';
import { Add, DeleteOutline } from '@mui/icons-material';
import { SearchCategory } from '~/types/enum';
import {
  getEduCode,
  sortGrade,
  sortHighSchoolGrade,
  sortSchoolYearsNewestFirstReadable,
  subjectsSort,
} from '~/components/Global/ResourceSearch/resourceSearchSortHelpers';
import { fetchMergedDropdownByEduAndSubjects } from './latestNewsDropdownFetch';

type TProps = {
  open: boolean;
  mode: 'create' | 'edit';
  initial: TLatestNewsItem | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

const MAX_ATTACHMENTS = 8;
const MAX_TOTAL_ATTACHMENT_BYTES = 15 * 1024 * 1024;

const ALL_PROGRAM_CODES: resourceEduCode[] = [
  resourceEduCode.E,
  resourceEduCode.J,
  resourceEduCode.H,
];

/** 多選下拉「全部」列的 MenuItem value（不寫入 draft） */
const LATEST_NEWS_SELECT_ALL_MENU_VALUE = '__LATEST_NEWS_ALL__';

const eduSortKeyForSubjects = (codes: resourceEduCode[]): resourceEduCode => {
  if (codes.includes(resourceEduCode.H)) return resourceEduCode.H;
  if (codes.includes(resourceEduCode.J)) return resourceEduCode.J;
  return resourceEduCode.E;
};

const emptyDraft = (): TLatestNewsItem => ({
  id: '',
  title: '',
  summary: '',
  contentHtml: '',
  tags: [],
  publishedAt: new Date().toISOString().slice(0, 10),
  takeDownAt: null,
  eduCodes: [resourceEduCode.E],
  subjects: [],
  schoolYears: [],
  grades: [],
  coverImageUrl: '',
  coverMediaId: '',
  mediaType: null,
  mediaDataUrl: null,
  youtubeUrl: '',
  attachments: [],
  seoMetaTitle: '',
  seoMetaDescription: '',
  seoOgImageUrl: '',
  status: 'draft',
  sortAssist: null,
  createdAt: Date.now(),
  updatedAt: Date.now(),
  createdBy: '',
  updatedBy: '',
});

const dataUrlToBlob = (dataUrl: string): Blob => {
  const idx = dataUrl.indexOf(',');
  if (idx < 0) throw new Error('圖片編碼格式不正確');
  const head = dataUrl.slice(0, idx);
  const b64 = dataUrl.slice(idx + 1);
  const mime = head.match(/data:([^;]+)/)?.[1] || 'application/octet-stream';
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) u8[i] = bin.charCodeAt(i);
  return new Blob([u8], { type: mime });
};

const assertApiOk = (r: { err: number; msg?: string }) => {
  if (r.err !== 0) throw new Error(r.msg || `API 錯誤（${r.err}）`);
};

const pickSubmitErrorRawMessage = (e: unknown): string => {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && 'data' in e) {
    const d = (e as { data: unknown }).data;
    if (typeof d === 'string') return d;
    if (typeof d === 'object' && d !== null) {
      const o = d as { message?: unknown; msg?: unknown };
      if (typeof o.message === 'string') return o.message;
      if (typeof o.msg === 'string') return o.msg;
    }
  }
  return '';
};

export const LatestNewsEditDialog = ({
  open,
  mode,
  initial,
  onClose,
  onSaved,
}: TProps) => {
  /**
   * 父層僅在 dialogOpen 時掛載本元件，關閉即卸載，故初次 render 就必須從 props 建立正確 draft，
   * 不可依賴「關閉後仍留在 state 裡的舊 draft」再靠 useEffect 晚一幀重設（否則 Quill 會吃到舊 HTML）。
   * 新增模式也在此建立 id，避免另起 useEffect 再 setDraft 造成一幀空白或與編輯競態。
   */
  const [draft, setDraft] = useState<TLatestNewsItem>(() => {
    if (mode === 'edit' && initial) {
      return snapshotLatestNewsItemForEdit(initial);
    }
    const now = Date.now();
    return {
      ...emptyDraft(),
      id: uuidv4(),
      createdAt: now,
      updatedAt: now,
    };
  });

  /** 送出／驗證時務必讀此 ref，避免 async 流程中仍握著舊 closure 的 draft 而送出空學程／科目等 */
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const [titleError, setTitleError] = useState('');
  const [youtubeError, setYoutubeError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverFileError, setCoverFileError] = useState('');
  const [attachmentErrById, setAttachmentErrById] = useState<
    Record<string, string>
  >({});
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const saveErrorRef = useRef<HTMLDivElement | null>(null);

  const [subjectOptions, setSubjectOptions] = useState<string[]>([]);
  const [yearOptions, setYearOptions] = useState<string[]>([]);
  const [gradeOptions, setGradeOptions] = useState<string[]>([]);

  const [uploadImage] = useArticlesUploadImageMutation();
  const [uploadAttachment] = useArticlesUploadAttachmentMutation();
  const [createArticle] = useArticlesCreateMutation();
  const [updateArticle] = useArticlesUpdateMutation();
  const [fetchSubjects] = useLazyDropDownListQuery();
  const [fetchYears] = useLazyDropDownListQuery();
  const [fetchGrades] = useLazyDropDownListQuery();

  const validateYoutube = (raw: string): string => {
    const v = raw.trim();
    if (!v) return '';
    if (youtubeEmbedId(v)) return '';
    return '僅支援 Youtube 連結（含 11 碼影片 ID），不支援其他影片或連結。';
  };

  useEffect(() => {
    const codes = draft.eduCodes;
    if (!codes.length) {
      setSubjectOptions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const results = await Promise.all(
          codes.map(edu =>
            fetchSubjects({
              isLaunch: true,
              categories: [SearchCategory.SCHOOL],
              resourceProps: ['subjects'],
              search: { eduCodes: edu },
            }).unwrap(),
          ),
        );
        if (cancelled) return;
        const merged = new Set<string>();
        for (const res of results) {
          if (res.err !== 0) continue;
          (res.data?.uniqResourceProps?.subjects ?? []).forEach(s =>
            merged.add(s),
          );
        }
        setSubjectOptions([...merged]);
      } catch {
        if (!cancelled) setSubjectOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draft.eduCodes, fetchSubjects]);

  /**
   * 學年度：UAT 需帶 subjects 才有選項；全選時不可一次打出學程×科目全部請求（易失敗），改分批合併。
   */
  useEffect(() => {
    const codes = draft.eduCodes;
    const subs = draft.subjects.map(s => s.trim()).filter(Boolean);
    if (!codes.length || !subs.length) {
      setYearOptions([]);
      return;
    }
    const gradeFilter =
      draft.grades.length === 1 && draft.grades[0].trim()
        ? draft.grades[0].trim()
        : undefined;
    let cancelled = false;
    void (async () => {
      try {
        const merged = await fetchMergedDropdownByEduAndSubjects(
          codes,
          subs,
          'years',
          fetchYears,
          (edu, subj) => ({
            eduCodes: edu,
            subjects: subj,
            grades: gradeFilter,
          }),
        );
        if (!cancelled) setYearOptions(merged);
      } catch {
        if (!cancelled) setYearOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draft.eduCodes, draft.subjects, draft.grades, fetchYears]);

  useEffect(() => {
    const codes = draft.eduCodes;
    const subs = draft.subjects.map(s => s.trim()).filter(Boolean);
    if (!codes.length || !subs.length) {
      setGradeOptions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const merged = await fetchMergedDropdownByEduAndSubjects(
          codes,
          subs,
          'grades',
          fetchGrades,
          (edu, subj) => ({ eduCodes: edu, subjects: subj }),
        );
        if (!cancelled) setGradeOptions(merged);
      } catch {
        if (!cancelled) setGradeOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draft.eduCodes, draft.subjects, fetchGrades]);

  const subjectMenuValues = useMemo(() => {
    const selected = draft.subjects.map(s => s.trim()).filter(Boolean);
    const base = subjectsSort(
      [...subjectOptions],
      getEduCode(eduSortKeyForSubjects(draft.eduCodes)),
    );
    for (const s of selected) {
      if (s && !base.includes(s)) base.unshift(s);
    }
    return base;
  }, [subjectOptions, draft.subjects, draft.eduCodes]);

  const yearMenuValues = useMemo(() => {
    const selected = draft.schoolYears.map(s => s.trim()).filter(Boolean);
    const base = sortSchoolYearsNewestFirstReadable([...yearOptions]);
    for (const y of selected) {
      if (y && !base.includes(y)) base.unshift(y);
    }
    return base;
  }, [yearOptions, draft.schoolYears]);

  const gradeMenuValues = useMemo(() => {
    const selected = draft.grades.map(s => s.trim()).filter(Boolean);
    const raw = [...gradeOptions];
    const primarySubj =
      draft.subjects.map(s => s.trim()).filter(Boolean)[0] ?? '';
    const sorted = draft.eduCodes.includes(resourceEduCode.H)
      ? sortHighSchoolGrade(primarySubj, raw)
      : sortGrade(raw) || [];
    const base = [...sorted];
    for (const g of selected) {
      if (g && !base.includes(g)) base.unshift(g);
    }
    return base;
  }, [gradeOptions, draft.grades, draft.eduCodes, draft.subjects]);

  const handlePickMedia = useCallback(() => {
    mediaInputRef.current?.click();
  }, []);

  const handleMediaFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      const err = validateLatestNewsCoverPick(file);
      if (err) {
        setCoverFileError(err);
        return;
      }
      setCoverFileError('');
      setCoverImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setDraft(prev => ({
          ...prev,
          mediaType: 'image',
          mediaDataUrl: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    },
    [],
  );

  const clearMedia = useCallback(() => {
    setCoverFileError('');
    setCoverImageFile(null);
    setDraft(prev => ({
      ...prev,
      coverImageUrl: '',
      mediaType: null,
      mediaDataUrl: null,
    }));
  }, []);

  const updateAttachment = (
    id: string,
    patch: Partial<TLatestNewsAttachment>,
  ) => {
    setDraft(prev => ({
      ...prev,
      attachments: prev.attachments.map(a =>
        a.id === id ? { ...a, ...patch } : a,
      ),
    }));
  };

  const removeAttachment = (id: string) => {
    setAttachmentErrById(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDraft(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a.id !== id),
    }));
  };

  const addAttachment = () => {
    if (draft.attachments.length >= MAX_ATTACHMENTS) return;
    const sort = draft.attachments.reduce((m, a) => Math.max(m, a.sort), 0) + 1;
    setDraft(prev => ({
      ...prev,
      attachments: [
        ...prev.attachments,
        {
          id: uuidv4(),
          fileName: '',
          url: '',
          sizeBytes: 0,
          mime: 'application/octet-stream',
          sort,
        },
      ],
    }));
  };

  const handleSubmit = () => {
    void (async () => {
      const submitDraft = draftRef.current;
      const t = submitDraft.title.trim();
      if (!t) {
        setTitleError('請輸入標題');
        queueMicrotask(() =>
          titleInputRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          }),
        );
        return;
      }
      const requiredMsgs = getLatestNewsRequiredViolations(submitDraft);
      if (requiredMsgs.length > 0) {
        setSubmitError(requiredMsgs.join('、'));
        queueMicrotask(() =>
          saveErrorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          }),
        );
        return;
      }
      const ytErr = validateYoutube(submitDraft.youtubeUrl || '');
      if (ytErr) {
        setYoutubeError(ytErr);
        setSubmitError(ytErr);
        queueMicrotask(() =>
          saveErrorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          }),
        );
        return;
      }
      if (submitDraft.mediaType === 'video' && submitDraft.mediaDataUrl) {
        setSubmitError(
          '封面僅支援圖檔；請移除預覽影片，或改填上方 Youtube 連結。',
        );
        queueMicrotask(() =>
          saveErrorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          }),
        );
        return;
      }
      if (submitDraft.attachments.length > MAX_ATTACHMENTS) {
        setSubmitError(`附件最多 ${MAX_ATTACHMENTS} 個`);
        queueMicrotask(() =>
          saveErrorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          }),
        );
        return;
      }
      const totalBytes = submitDraft.attachments.reduce((s, a) => {
        if (a.pendingFile) return s + a.pendingFile.size;
        return s + (a.sizeBytes || 0);
      }, 0);
      if (totalBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
        setSubmitError('單篇附件合計大小不可超過 15 MB');
        queueMicrotask(() =>
          saveErrorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          }),
        );
        return;
      }
      if (coverImageFile) {
        const coverPickErr = validateLatestNewsCoverPick(coverImageFile);
        if (coverPickErr) {
          setSubmitError(coverPickErr);
          queueMicrotask(() =>
            saveErrorRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            }),
          );
          return;
        }
      }
      for (const a of submitDraft.attachments) {
        if (!a.pendingFile) continue;
        const attErr = validateLatestNewsAttachmentPick(a.pendingFile);
        if (attErr) {
          setSubmitError(attErr);
          queueMicrotask(() =>
            saveErrorRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            }),
          );
          return;
        }
      }
      setSubmitError('');
      setSaving(true);
      try {
        let coverUrl = submitDraft.coverImageUrl.trim();
        if (coverImageFile) {
          const fd = new FormData();
          fd.append('file', coverImageFile);
          const up = await uploadImage(fd).unwrap();
          assertApiOk(up);
          coverUrl = up.data?.url ?? '';
        } else if (submitDraft.mediaDataUrl?.startsWith('data:image/')) {
          const blob = dataUrlToBlob(submitDraft.mediaDataUrl);
          const blobMimeErr = validateLatestNewsCoverBlobMime(blob.type);
          if (blobMimeErr) throw new Error(blobMimeErr);
          const ext =
            blob.type === 'image/png'
              ? 'png'
              : blob.type === 'image/webp'
                ? 'webp'
                : 'jpg';
          const fd = new FormData();
          fd.append(
            'file',
            new File([blob], `cover.${ext}`, {
              type: blob.type || 'image/jpeg',
            }),
          );
          const up = await uploadImage(fd).unwrap();
          assertApiOk(up);
          coverUrl = up.data?.url ?? '';
        }

        const resolvedAttachments: TLatestNewsAttachment[] = [];
        for (const att of submitDraft.attachments) {
          if (att.pendingFile) {
            const fd = new FormData();
            fd.append('file', att.pendingFile);
            const up = await uploadAttachment(fd).unwrap();
            assertApiOk(up);
            const d = up.data;
            if (!d?.url) throw new Error('附件上傳未回傳 URL');
            resolvedAttachments.push({
              ...att,
              url: d.url,
              fileName: d.fileName || att.pendingFile.name,
              sizeBytes: d.fileSize,
              mime: att.pendingFile.type || att.mime,
              pendingFile: undefined,
            });
          } else {
            resolvedAttachments.push({ ...att, pendingFile: undefined });
          }
        }

        const attApi = attachmentsToApi(resolvedAttachments);
        const now = Date.now();
        const payloadBase: TLatestNewsItem = {
          ...submitDraft,
          attachments: resolvedAttachments,
          title: t,
          summary: submitDraft.summary.trim(),
          seoMetaTitle: submitDraft.seoMetaTitle.trim() || t,
          seoMetaDescription: submitDraft.seoMetaDescription.trim() || t,
          updatedAt: now,
          createdAt: mode === 'create' ? now : submitDraft.createdAt,
        };

        if (mode === 'create') {
          const body = latestNewsToCreatePayload(payloadBase, coverUrl, attApi);
          const res = await createArticle(body).unwrap();
          assertApiOk(res);
        } else {
          const body = latestNewsToUpdatePayload(payloadBase, coverUrl, attApi);
          const res = await updateArticle(body).unwrap();
          assertApiOk(res);
        }

        await onSaved();
      } catch (e) {
        const rawMsg = pickSubmitErrorRawMessage(e);
        const hints = getLatestNewsRequiredViolations(draftRef.current);
        const genericServer =
          rawMsg && /^internal server error$/i.test(rawMsg.trim());
        const message =
          genericServer && hints.length > 0
            ? hints.join('、')
            : rawMsg
              ? formatArticleBackendValidationMessage(rawMsg)
              : '儲存失敗，請稍後再試。';
        setSubmitError(message);
        queueMicrotask(() =>
          saveErrorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          }),
        );
      } finally {
        setSaving(false);
      }
    })();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      hideBackdrop
      scroll="paper"
      PaperProps={{
        elevation: 0,
        sx: {
          m: 0,
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0, px: 1.5, py: 1.5 }}>
        {mode === 'create' ? '新增最新消息' : '編輯最新消息'}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'auto',
          px: 1.5,
          py: 2,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          基本與狀態
        </Typography>
        <TextField
          label="標題"
          fullWidth
          margin="dense"
          value={draft.title}
          inputRef={titleInputRef}
          inputProps={{ maxLength: LATEST_NEWS_TITLE_MAX_LEN }}
          onChange={e => {
            setTitleError('');
            setDraft(prev => ({ ...prev, title: e.target.value }));
          }}
          error={Boolean(titleError)}
          helperText={titleError || undefined}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="ln-status-label">狀態</InputLabel>
          <Select
            labelId="ln-status-label"
            label="狀態"
            value={draft.status}
            onChange={e =>
              setDraft(prev => ({
                ...prev,
                status: e.target.value as 'draft' | 'published',
              }))
            }
          >
            <MenuItem value="draft">草稿</MenuItem>
            <MenuItem value="published">已發布</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          上架日期
        </Typography>
        <TextField
          label="上架日期"
          type="date"
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          value={draft.publishedAt}
          onChange={e =>
            setDraft(prev => ({ ...prev, publishedAt: e.target.value }))
          }
        />

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          學程、科目、學年度、年級
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 2,
            columnGap: 2,
            rowGap: 1.5,
          }}
        >
          <FormControl fullWidth margin="none">
            <InputLabel id="ln-edu-label">學程</InputLabel>
            <Select
              labelId="ln-edu-label"
              label="學程"
              multiple
              value={draft.eduCodes}
              onChange={(e: SelectChangeEvent<resourceEduCode[]>) => {
                const raw = e.target.value;
                const vals = typeof raw === 'string' ? raw.split(',') : raw;
                if (vals.includes(LATEST_NEWS_SELECT_ALL_MENU_VALUE)) {
                  setDraft(prev => {
                    const allOn =
                      ALL_PROGRAM_CODES.length > 0 &&
                      ALL_PROGRAM_CODES.every(c => prev.eduCodes.includes(c));
                    return {
                      ...prev,
                      eduCodes: allOn ? [] : [...ALL_PROGRAM_CODES],
                      subjects: [],
                      schoolYears: [],
                      grades: [],
                    };
                  });
                  return;
                }
                setDraft(prev => ({
                  ...prev,
                  eduCodes: vals as resourceEduCode[],
                  subjects: [],
                  schoolYears: [],
                  grades: [],
                }));
              }}
              renderValue={selected => {
                if (!selected.length) return <em>（未指定）</em>;
                return selected.join('、');
              }}
            >
              <MenuItem value={LATEST_NEWS_SELECT_ALL_MENU_VALUE}>
                <Checkbox
                  checked={
                    ALL_PROGRAM_CODES.length > 0 &&
                    ALL_PROGRAM_CODES.every(c => draft.eduCodes.includes(c))
                  }
                  indeterminate={
                    draft.eduCodes.length > 0 &&
                    !ALL_PROGRAM_CODES.every(c => draft.eduCodes.includes(c))
                  }
                />
                <ListItemText primary="全部" />
              </MenuItem>
              {ALL_PROGRAM_CODES.map(code => (
                <MenuItem key={code} value={code}>
                  <Checkbox checked={draft.eduCodes.includes(code)} />
                  <ListItemText primary={code} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="none">
            <InputLabel id="ln-subject-label">科目</InputLabel>
            <Select
              labelId="ln-subject-label"
              label="科目"
              multiple
              value={draft.subjects}
              disabled={!draft.eduCodes.length}
              onChange={(e: SelectChangeEvent<string[]>) => {
                const raw = e.target.value;
                const vals =
                  typeof raw === 'string'
                    ? raw.split(',').filter(Boolean)
                    : raw;
                if (vals.includes(LATEST_NEWS_SELECT_ALL_MENU_VALUE)) {
                  setDraft(prev => {
                    const allOn =
                      subjectMenuValues.length > 0 &&
                      subjectMenuValues.every(s => prev.subjects.includes(s));
                    return {
                      ...prev,
                      subjects: allOn ? [] : [...subjectMenuValues],
                      schoolYears: [],
                      grades: [],
                    };
                  });
                  return;
                }
                setDraft(prev => ({
                  ...prev,
                  subjects: vals,
                  schoolYears: [],
                  grades: [],
                }));
              }}
              renderValue={selected => {
                if (!selected.length) return <em>（未指定）</em>;
                if (selected.length <= 2) return selected.join('、');
                return `已選 ${selected.length} 項`;
              }}
            >
              <MenuItem value={LATEST_NEWS_SELECT_ALL_MENU_VALUE}>
                <Checkbox
                  checked={
                    subjectMenuValues.length > 0 &&
                    subjectMenuValues.every(s => draft.subjects.includes(s))
                  }
                  indeterminate={
                    draft.subjects.length > 0 &&
                    !subjectMenuValues.every(s => draft.subjects.includes(s))
                  }
                />
                <ListItemText primary="全部" />
              </MenuItem>
              {subjectMenuValues.map(opt => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox checked={draft.subjects.includes(opt)} />
                  <ListItemText primary={opt} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="none">
            <InputLabel id="ln-year-label">學年度</InputLabel>
            <Select
              labelId="ln-year-label"
              label="學年度"
              multiple
              value={draft.schoolYears}
              disabled={!draft.subjects.some(s => s.trim())}
              onChange={(e: SelectChangeEvent<string[]>) => {
                const raw = e.target.value;
                const vals =
                  typeof raw === 'string'
                    ? raw.split(',').filter(Boolean)
                    : raw;
                if (vals.includes(LATEST_NEWS_SELECT_ALL_MENU_VALUE)) {
                  setDraft(prev => {
                    const allOn =
                      yearMenuValues.length > 0 &&
                      yearMenuValues.every(y => prev.schoolYears.includes(y));
                    return {
                      ...prev,
                      schoolYears: allOn ? [] : [...yearMenuValues],
                      grades: [],
                    };
                  });
                  return;
                }
                setDraft(prev => ({
                  ...prev,
                  schoolYears: vals,
                  grades: [],
                }));
              }}
              renderValue={selected => {
                if (!selected.length) return <em>（未指定）</em>;
                if (selected.length <= 2) return selected.join('、');
                return `已選 ${selected.length} 項`;
              }}
            >
              <MenuItem value={LATEST_NEWS_SELECT_ALL_MENU_VALUE}>
                <Checkbox
                  checked={
                    yearMenuValues.length > 0 &&
                    yearMenuValues.every(y => draft.schoolYears.includes(y))
                  }
                  indeterminate={
                    draft.schoolYears.length > 0 &&
                    !yearMenuValues.every(y => draft.schoolYears.includes(y))
                  }
                />
                <ListItemText primary="全部" />
              </MenuItem>
              {yearMenuValues.map(opt => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox checked={draft.schoolYears.includes(opt)} />
                  <ListItemText primary={opt} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="none">
            <InputLabel id="ln-grade-label">年級（冊次）</InputLabel>
            <Select
              labelId="ln-grade-label"
              label="年級（冊次）"
              multiple
              value={draft.grades}
              disabled={!draft.subjects.some(s => s.trim())}
              onChange={(e: SelectChangeEvent<string[]>) => {
                const raw = e.target.value;
                const vals =
                  typeof raw === 'string'
                    ? raw.split(',').filter(Boolean)
                    : raw;
                if (vals.includes(LATEST_NEWS_SELECT_ALL_MENU_VALUE)) {
                  setDraft(prev => {
                    const allOn =
                      gradeMenuValues.length > 0 &&
                      gradeMenuValues.every(g => prev.grades.includes(g));
                    return {
                      ...prev,
                      grades: allOn ? [] : [...gradeMenuValues],
                    };
                  });
                  return;
                }
                setDraft(prev => ({ ...prev, grades: vals }));
              }}
              renderValue={selected => {
                if (!selected.length) return <em>（未指定）</em>;
                if (selected.length <= 2) return selected.join('、');
                return `已選 ${selected.length} 項`;
              }}
            >
              <MenuItem value={LATEST_NEWS_SELECT_ALL_MENU_VALUE}>
                <Checkbox
                  checked={
                    gradeMenuValues.length > 0 &&
                    gradeMenuValues.every(g => draft.grades.includes(g))
                  }
                  indeterminate={
                    draft.grades.length > 0 &&
                    !gradeMenuValues.every(g => draft.grades.includes(g))
                  }
                />
                <ListItemText primary="全部" />
              </MenuItem>
              {gradeMenuValues.map(opt => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox checked={draft.grades.includes(opt)} />
                  <ListItemText primary={opt} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          封面與影片
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'flex-start',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: '1 1 320px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <TextField
              label="Youtube 連結"
              fullWidth
              margin="none"
              value={draft.youtubeUrl}
              onChange={e => {
                const next = e.target.value;
                setYoutubeError(validateYoutube(next));
                setDraft(prev => ({ ...prev, youtubeUrl: next }));
              }}
              placeholder="https://www.youtube.com/watch?v=…"
              error={Boolean(youtubeError)}
              helperText={
                youtubeError ||
                '選填。僅支援 Youtube 連結；可與封面圖並存。兩者皆未填時，前台顯示預設封面圖。'
              }
            />
          </Box>
          <Box
            sx={{
              flex: '1 1 260px',
              minWidth: 0,
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              封面圖
            </Typography>
            {coverFileError ? (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', mb: 1 }}
              >
                {coverFileError}
              </Typography>
            ) : null}
            <input
              ref={mediaInputRef}
              type="file"
              accept={LATEST_NEWS_COVER_FILE_ACCEPT}
              hidden
              onChange={handleMediaFile}
            />
            <Box
              sx={{
                border: theme => `1px dashed ${theme.palette.divider}`,
                borderRadius: 1,
                minHeight: 112,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                py: 1.25,
                px: 1.5,
                bgcolor: 'background.paper',
              }}
            >
              {draft.mediaDataUrl && draft.mediaType === 'image' && (
                <Box
                  component="img"
                  src={draft.mediaDataUrl}
                  alt=""
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 128,
                    objectFit: 'contain',
                  }}
                />
              )}
              {!draft.mediaDataUrl &&
                draft.coverImageUrl.trim() &&
                (!draft.mediaType || draft.mediaType === 'image') && (
                  <Box
                    component="img"
                    src={resolveOneBoxV3MediaUrl(draft.coverImageUrl)}
                    alt=""
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 128,
                      objectFit: 'contain',
                    }}
                  />
                )}
              {draft.mediaDataUrl && draft.mediaType === 'video' && (
                <Box
                  component="video"
                  src={draft.mediaDataUrl}
                  controls
                  sx={{ maxWidth: '100%', maxHeight: 128 }}
                />
              )}
              {(() => {
                const hasImagePreview =
                  Boolean(draft.mediaDataUrl && draft.mediaType === 'image') ||
                  Boolean(
                    !draft.mediaDataUrl &&
                    draft.coverImageUrl.trim() &&
                    (!draft.mediaType || draft.mediaType === 'image'),
                  );
                const hasVideoPreview = Boolean(
                  draft.mediaDataUrl && draft.mediaType === 'video',
                );
                if (hasImagePreview || hasVideoPreview) return null;
                return (
                  <Typography variant="body2" color="text.secondary">
                    尚未選擇檔案
                  </Typography>
                );
              })()}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  onClick={handlePickMedia}
                >
                  上傳圖片
                </Button>
                {(Boolean(draft.mediaDataUrl && draft.mediaType === 'image') ||
                  Boolean(
                    !draft.mediaDataUrl &&
                    draft.coverImageUrl.trim() &&
                    (!draft.mediaType || draft.mediaType === 'image'),
                  )) && (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={clearMedia}
                  >
                    刪除圖片
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 1, px: 0 }}
        >
          內文
        </Typography>
        <Box
          sx={theme => ({
            mx: -1.5,
            width: `calc(100% + ${theme.spacing(3)})`,
            minWidth: 0,
            maxWidth: 'none',
          })}
        >
          <RichTextEditor
            value={draft.contentHtml}
            onChange={html =>
              setDraft(prev => ({ ...prev, contentHtml: html }))
            }
            editorMinHeightPx={480}
          />
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          附件（最多 {MAX_ATTACHMENTS} 個，合計 ≤ 15 MB）
        </Typography>
        <Stack spacing={1.5}>
          {draft.attachments.map(att => (
            <Paper key={att.id} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={() => removeAttachment(att.id)}
                >
                  移除
                </Button>
              </Stack>
              <Button
                component="label"
                size="small"
                variant="outlined"
                sx={{ mb: 1 }}
              >
                選擇檔案上傳（uploadAttachment）
                <input
                  type="file"
                  accept={LATEST_NEWS_ATTACHMENT_FILE_ACCEPT}
                  hidden
                  onChange={e => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (!f) return;
                    const attErr = validateLatestNewsAttachmentPick(f);
                    if (attErr) {
                      setAttachmentErrById(prev => ({
                        ...prev,
                        [att.id]: attErr,
                      }));
                      return;
                    }
                    setAttachmentErrById(prev => {
                      const next = { ...prev };
                      delete next[att.id];
                      return next;
                    });
                    updateAttachment(att.id, {
                      pendingFile: f,
                      fileName: f.name,
                      sizeBytes: f.size,
                      mime: f.type || 'application/octet-stream',
                    });
                  }}
                />
              </Button>
              {attachmentErrById[att.id] ? (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', mb: 1 }}
                >
                  {attachmentErrById[att.id]}
                </Typography>
              ) : null}
              {att.pendingFile && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  已選擇「{att.pendingFile.name}」，儲存時上傳並帶入後端回傳的
                  URL。
                </Typography>
              )}
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 1,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  bgcolor: theme =>
                    theme.palette.mode === 'dark'
                      ? 'action.selected'
                      : 'grey.50',
                  px: 1.5,
                  py: 1,
                }}
              >
                <Stack divider={<Divider />} spacing={1.25}>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      檔名
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.25, wordBreak: 'break-word' }}
                    >
                      {att.fileName.trim() || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      URL
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.25, wordBreak: 'break-all' }}
                    >
                      {att.url.trim() || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      大小（位元組）
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                      {att.sizeBytes ? String(att.sizeBytes) : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      MIME
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.25, wordBreak: 'break-word' }}
                    >
                      {att.mime.trim() || '—'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          ))}
        </Stack>
        <Button
          startIcon={<Add />}
          onClick={addAttachment}
          disabled={draft.attachments.length >= MAX_ATTACHMENTS}
          sx={{ mt: 1 }}
        >
          新增附件
        </Button>
      </DialogContent>
      <DialogActions
        sx={{
          flexShrink: 0,
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
          px: 1.5,
          py: 1.5,
        }}
      >
        <Box ref={saveErrorRef} sx={{ width: '100%' }}>
          {submitError ? (
            <Typography color="error" variant="body2">
              {submitError}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            width: '100%',
          }}
        >
          <Button onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? '儲存中…' : '儲存'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
